import { useCallback, useMemo, useRef, useState } from "react";
import axios from "axios";
import { baseUrl } from "../assets/Animations";
import { getRequestHeaders } from "../api";

const DEFAULT_BUCKET = "17:00-18:00"; // weekday 5pm bucket

const makeKey = (schoolId, bucket, fromKey, toKey) =>
  `${schoolId || "na"}|${bucket || DEFAULT_BUCKET}|${fromKey}=>${toKey}`;

const normalizeDriveMinutes = (value) => {
  if (
    value == null ||
    (typeof value === "string" && value.trim() === "")
  ) {
    return null;
  }
  const minutes = Number(value);
  return Number.isFinite(minutes) && minutes >= 0 ? minutes : null;
};

export const mergeSeedEdges = (
  previous,
  edges,
  schoolId,
  bucket = DEFAULT_BUCKET
) => {
  const next = new Map(previous || []);
  (edges || []).forEach((edge) => {
    const originKey = edge.origin?.key || edge.origin || edge.fromKey || edge.from;
    const destKey = edge.destination?.key || edge.destination || edge.toKey || edge.to;
    const school = edge.schoolId || schoolId;
    const tod = edge.todBucket || edge.bucket || bucket;
    if (!originKey || !destKey || !school || !tod) return;
    const minutes = normalizeDriveMinutes(edge.minutes);
    if (minutes == null) return;
    next.set(makeKey(school, tod, originKey, destKey), minutes);
  });
  return next;
};

export default function useDriveMatrix(schoolId, bucket = DEFAULT_BUCKET) {
  const [minutesMap, setMinutesMap] = useState(new Map());
  const pending = useRef(new Map());

  const ensureEdge = useCallback(async () => null, []);

  const minutesBetween = useCallback(
    (fromKey, toKey) => {
      if (!fromKey || !toKey || !schoolId) return null;
      if (String(fromKey) === String(toKey)) return 0;
      const key = makeKey(schoolId, bucket, fromKey, toKey);
      return minutesMap.get(key);
    },
    [bucket, minutesMap, schoolId]
  );

  const ensureAllPairs = useCallback(
    async (input) => {
      if (!schoolId) return [];
      let pairs = [];
      if (Array.isArray(input) && input.length > 0 && input[0]?.fromKey) {
        pairs = input.filter(
          (p) =>
            p.fromKey &&
            p.toKey &&
            String(p.fromKey) !== String(p.toKey)
        );
      } else if (Array.isArray(input)) {
        const uniqueKeys = Array.from(new Set(input.filter(Boolean)));
        if (uniqueKeys.length < 2) return [];
        for (let i = 0; i < uniqueKeys.length; i++) {
          for (let j = 0; j < uniqueKeys.length; j++) {
            if (i === j) continue;
            const fromKey = uniqueKeys[i];
            const toKey = uniqueKeys[j];
            pairs.push({ fromKey, toKey });
          }
        }
      } else {
        return [];
      }

      const uniquePairs = new Map();
      pairs.forEach((pair) => {
        const key = makeKey(schoolId, bucket, pair.fromKey, pair.toKey);
        if (!uniquePairs.has(key)) uniquePairs.set(key, pair);
      });

      const requestsToAwait = new Set();
      const newPairs = [];
      uniquePairs.forEach((pair, key) => {
        if (minutesMap.has(key)) return;
        const existingRequest = pending.current.get(key);
        if (existingRequest) {
          requestsToAwait.add(existingRequest);
          return;
        }
        newPairs.push(pair);
      });

      let ownRequest = null;
      if (newPairs.length) {
        const body = {
          schoolId,
          bucket,
          todBucket: bucket,
          pairs: newPairs,
        };
        ownRequest = axios
          .post(`${baseUrl}/drive-edges/fill`, body, {
            headers: {
              "Content-Type": "application/json",
              ...(getRequestHeaders()?.headers || {}),
            },
          })
          .then((res) => {
            const edges = Array.isArray(res.data) ? res.data : res.data?.edges || [];
            setMinutesMap((prev) => {
              const next = new Map(prev);
              edges.forEach((edge) => {
                const k = makeKey(
                  edge.schoolId || schoolId,
                  edge.todBucket || edge.bucket || bucket,
                  edge.origin?.key || edge.origin || edge.fromKey || edge.from,
                  edge.destination?.key ||
                    edge.destination ||
                    edge.toKey ||
                    edge.to
                );
                const minutes = normalizeDriveMinutes(edge.minutes);
                if (minutes != null) next.set(k, minutes);
              });
              return next;
            });
            return edges;
          })
          .catch(() => []);

        newPairs.forEach(({ fromKey, toKey }) => {
          const key = makeKey(schoolId, bucket, fromKey, toKey);
          pending.current.set(key, ownRequest);
        });
        requestsToAwait.add(ownRequest);
      }

      if (!requestsToAwait.size) return [];

      let edgeGroups;
      try {
        edgeGroups = await Promise.all(Array.from(requestsToAwait));
      } finally {
        if (ownRequest) {
          newPairs.forEach(({ fromKey, toKey }) => {
            const key = makeKey(schoolId, bucket, fromKey, toKey);
            if (pending.current.get(key) === ownRequest) {
              pending.current.delete(key);
            }
          });
        }
      }

      const resultEdges = [];
      const seenEdges = new Set();
      edgeGroups.flat().forEach((edge) => {
        if (!edge) return;
        const fromKey =
          edge.origin?.key || edge.origin || edge.fromKey || edge.from;
        const toKey =
          edge.destination?.key || edge.destination || edge.toKey || edge.to;
        const key = `${fromKey}=>${toKey}`;
        if (seenEdges.has(key)) return;
        seenEdges.add(key);
        resultEdges.push(edge);
      });
      return resultEdges;
    },
    [bucket, minutesMap, schoolId]
  );

  const seedEdges = useCallback(
    (edges) => {
      if (!Array.isArray(edges) || !edges.length) return;
      // Drive times are directional; only seed the direction returned by the API.
      setMinutesMap((prev) => mergeSeedEdges(prev, edges, schoolId, bucket));
    },
    [bucket, schoolId]
  );

  const api = useMemo(
    () => ({
      minutesBetween,
      minutesMap,
      ensureEdge,
      ensureAllPairs,
      seedEdges,
    }),
    [ensureEdge, ensureAllPairs, minutesBetween, minutesMap, seedEdges]
  );

  return api;
}
